import { Button, Flex, Stack, Tabs, Text, Title } from "@mantine/core";
import {
    BoltOutlined,
    EmailOutlined,
    InterestsOutlined,
    LabelOutlined,
    MedicalInformationOutlined,
    PublicOutlined,
    RequestQuoteOutlined,
    UploadOutlined,
    VolunteerActivismOutlined
} from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import {
    AppContext,
    AppContextType,
    AutoHeightContainer,
    ChipGroup,
    ChipListCard,
    ChipWithMenu,
    DrawerIf,
    FileAccordion,
    KeyValueTable,
    PriorityNotesList,
    PriorityStatus,
    PriorityStatusSummary,
    ProfileCard,
    TagsList,
    UpdateFileInput,
    calculateAge,
    confettiRandom,
    createShortAddressString,
    formatDate,
    formatLanguageLabel,
    titleCase,
    useDeleteFileMutation,
    useGetPresignedFileDownloadUrlMutation,
    useGetPriorityEntryDetailsQuery,
    useGetPriorityStatusesQuery,
    useTherapproveDrawers,
    useTherapproveModals,
    useUpdatePriorityAttributeMutation
} from "@therapprove/common";
import { UUID } from "crypto";
import { FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export const PriorityEntryDetails: FC = () => {
    // Hooks
    const { isTherapproveAdmin } = useContext(AppContext) as AppContextType;
    const { caregiver: caregiverDrawer, patient: patientDrawer } =
        useTherapproveDrawers();
    const {
        filePreview,
        fileUpload,
        globalInvite,
        manageProvidersForPatient,
        priorityReferral,
        priorityStatusSimpleEmail
    } = useTherapproveModals();
    const { priorityEntryId } = useParams();

    // State
    const [selectedEntryId, setSelectedEntryId] = useState<UUID>(
        priorityEntryId as UUID
    );

    // Priority Entry Details
    const [{ data }] = useGetPriorityEntryDetailsQuery({
        requestPolicy: "network-only",
        variables: {
            priorityEntryId: selectedEntryId
        }
    });
    const priorityEntry = data?.priorityEntries?.priorityEntry;
    const providers = priorityEntry?.providers;
    const patient = priorityEntry?.patient;
    const caregiver = priorityEntry?.caregiver;
    const relatedEntries = priorityEntry?.relatedEntries;

    // Priority Attribute
    const priorityAttribute = priorityEntry?.priorityAttribute;
    const [, updatePriorityAttribute] = useUpdatePriorityAttributeMutation();

    // Mutations
    const [, downloadFile] = useGetPresignedFileDownloadUrlMutation();
    const [, deleteFile] = useDeleteFileMutation();

    // This state exists so that the UI will change faster, instead of looking like we're lagging while waiting for the API call to complete
    const [priorityStatus, setPriorityStatus] =
        useState<PriorityStatusSummary | null>(
            priorityAttribute?.priorityStatus ?? null
        );

    // Priority Statuses
    const [{ data: priorityStatusesData }] = useGetPriorityStatusesQuery({});
    const priorityStatuses =
        priorityStatusesData?.priorityStatuses?.priorityStatuses ?? [];

    const applyStatusLocally = (status: PriorityStatusSummary) => {
        status.throwConfetti && confettiRandom();
        setPriorityStatus(status);
    };

    const statusMenuItems = priorityStatuses
        .map((status) => {
            if (!status) return null;
            return {
                value: status.name,
                color: status.colorHex,
                sortOrder: status.sortOrder,
                rightSection: status.simpleEmailTemplateId ? (
                    <SvgIcon
                        component={BoltOutlined}
                        style={{ fontSize: "1rem" }}
                    />
                ) : undefined,
                setCurrentItemOnClick: !status.simpleEmailTemplateId,
                onClick: () => {
                    if (
                        status.simpleEmailTemplateId &&
                        priorityAttribute?.priorityAttributeId
                    ) {
                        priorityStatusSimpleEmail.open({
                            priorityAttributeId:
                                priorityAttribute.priorityAttributeId,
                            priorityStatus: status,
                            onStatusChanged: applyStatusLocally
                        });
                        return;
                    }

                    handleSetStatus(status);
                }
            };
        })
        .filter((status) => status !== null);

    // Related Entries
    const hasManyRelatedEntries = relatedEntries && relatedEntries.length > 1;
    const therapyToHighlight = relatedEntries?.find(
        (entry) => entry?.priorityEntryId === selectedEntryId
    )?.therapyCategoryName;

    const canEditCaregiverOrPatient =
        !!priorityEntry?.isOriginatingOrganization ||
        !!priorityEntry?.isAdoptingOrganization ||
        !!priorityEntry?.isReferredOrganization ||
        isTherapproveAdmin;

    // Caregiver Details
    const caregiverName = `${caregiver?.firstName} ${caregiver?.lastName}`;

    // Patient Details
    const patientName = `${patient?.firstName} ${patient?.lastName}`;
    const patientDateOfBirth = patient?.dateOfBirth;
    const patientGradeLevel = patient?.gradeLevel;
    const patientAge = patientDateOfBirth && calculateAge(patientDateOfBirth);
    const patientLocation = createShortAddressString({
        city: patient?.postalCodeInfo?.city,
        stateId: patient?.postalCodeInfo?.stateId
    });
    const patientConditions = (patient?.conditionList ?? [])
        .map((condition) => condition?.label)
        .filter((label): label is string => !!label);
    const patientTherapies = (patient?.therapyCategoryList ?? [])
        .map((therapy) => therapy?.label)
        .filter((label): label is string => !!label);
    const patientSkills = (patient?.therapySubCategoryList ?? [])
        .map((skill) => skill?.label)
        .filter((label): label is string => !!label);
    const patientInsurances = (patient?.insuranceList ?? [])
        .map((insurance) => insurance?.label)
        .filter((label): label is string => !!label);

    // Sync local status state when viewing a different priority attribute (e.g. tab switch),
    // but not when graphcache updates scalar fields on the same entity — that would
    // overwrite our optimistic update since the mutation doesn't return the nested priorityStatus.
    useEffect(() => {
        setPriorityStatus(priorityAttribute?.priorityStatus ?? null);
    }, [priorityAttribute?.priorityAttributeId]);

    // Tags
    const titleCaseTags = priorityAttribute?.tags?.map((tag) => titleCase(tag));

    /**********************************
     * Handlers
     *********************************/

    // Handles the tab change for the related entries
    const handleTabChange = (entryId?: UUID | string | null) => {
        if (!entryId) return;
        setSelectedEntryId(entryId as UUID);
        // Update the URL without reloading the page
        window.history.pushState(null, "", `/referral-pipeline/${entryId}`);
    };

    // Sets the status for the menu and makes the API call to update
    const handleSetStatus = (status?: PriorityStatus | null) => {
        if (!status || !priorityAttribute?.priorityAttributeId) return;

        applyStatusLocally(status);
        updatePriorityAttribute({
            priorityAttributeId: priorityAttribute?.priorityAttributeId,
            priorityStatusId: status.priorityStatusId
        });
    };

    // Handles the opening of the upload file modal
    const handleOpenUploadFileModal = async (fileInfo: UpdateFileInput) => {
        fileUpload.open({ fileInfo });
    };

    // Handles the opening of the file preview modal
    const handleOpenFilePreviewModal = async (
        fileId: string,
        fileType: string,
        tags?: ReadonlyArray<string | null | undefined> | null
    ) => {
        const { data } = await downloadFile({ fileId });
        const url = data?.files?.downloadFilePresignedUrl?.presignedUrl;
        if (url) {
            filePreview.open({
                filePath: url,
                fileType,
                tags
            });
        }
    };

    // Handles the downloading of a file
    const handleDownloadFile = async (fileId: string, name: string) => {
        const { data } = await downloadFile({ fileId });
        const url = data?.files?.downloadFilePresignedUrl?.presignedUrl;
        if (url) {
            // Fetch the file as a Blob
            const response = await fetch(url);
            const blob = await response.blob();

            // Create a temporary object URL for the blob
            const blobUrl = URL.createObjectURL(blob);

            // Create a hidden anchor tag to trigger the download
            const link = document.createElement("a");
            link.href = blobUrl;

            link.download = name;

            // Append to body, trigger click, then clean up
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        }
    };

    // Handles the opening of a file in a new tab
    const handleNewTab = async (fileId: string) => {
        const { data } = await downloadFile({ fileId });
        const url = data?.files?.downloadFilePresignedUrl?.presignedUrl;
        if (url) {
            window.open(url, "_blank");
        }
    };

    // Handles the deletion of a file
    const handleDeleteFile = async (fileId: string) => {
        await deleteFile({ fileId });
    };

    return (
        <AutoHeightContainer>
            <Flex justify="flex-end" gap="xs" mb="xs">
                <Button
                    variant="subtle"
                    leftSection={<SvgIcon component={PublicOutlined} />}
                    onClick={() =>
                        globalInvite.open({
                            priorityEntryId: selectedEntryId,
                            therapyCategoryIds:
                                patient?.therapyCategoryList?.map(
                                    (therapy) => therapy?.value
                                ) ?? []
                        })
                    }
                >
                    Invite to Global Referrals
                </Button>
                <Button
                    variant="subtle"
                    leftSection={<SvgIcon component={EmailOutlined} />}
                    onClick={() =>
                        priorityReferral.open({
                            priorityEntryId: selectedEntryId
                        })
                    }
                >
                    Refer Client
                </Button>
                <Button
                    leftSection={<SvgIcon component={UploadOutlined} />}
                    onClick={() => fileUpload.open()}
                >
                    Upload Files
                </Button>
            </Flex>
            {hasManyRelatedEntries && (
                <Tabs defaultValue={selectedEntryId} mb="md">
                    <Tabs.List>
                        {relatedEntries?.map((entry) => {
                            if (!entry) return null;
                            return (
                                <Tabs.Tab
                                    key={entry.priorityEntryId}
                                    value={entry.priorityEntryId}
                                    onClick={() =>
                                        handleTabChange(entry.priorityEntryId)
                                    }
                                >
                                    {entry.therapyCategoryName}
                                </Tabs.Tab>
                            );
                        })}
                    </Tabs.List>
                </Tabs>
            )}
            <Flex
                flex={1}
                gap="sm"
                style={{ minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
                direction={{ base: "column", lg: "row" }}
            >
                <Stack gap="sm" style={{ minWidth: "300px", maxWidth: "100%" }}>
                    <ProfileCard
                        // Even though we can't edit priority entries, we need to pass this in so the component doesn't freak TF out.
                        // Need to destroy that thing and make a better one.
                        drawerInfo={priorityEntry}
                        options={{
                            showAvatar: false,
                            titleComponent: (
                                <Title
                                    order={4}
                                    style={{
                                        marginTop: "1rem",
                                        cursor: "pointer"
                                    }}
                                    onClick={() =>
                                        patientDrawer.open({
                                            patientId: priorityEntry?.patientId
                                        })
                                    }
                                >
                                    {patientName}
                                </Title>
                            ),
                            subTitleComponent: (
                                <Text
                                    variant="label-muted"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                        caregiverDrawer.open({
                                            caregiverId:
                                                priorityEntry?.caregiverId
                                        })
                                    }
                                >
                                    {caregiverName}
                                </Text>
                            ),
                            allowEdit: false
                        }}
                    />
                    <KeyValueTable
                        email={caregiver?.email}
                        phone={caregiver?.phoneNumber}
                        age={
                            patientAge &&
                            `${patientAge.years} years, ${patientAge.months} months`
                        }
                        date_of_birth={formatDate(patientDateOfBirth)}
                        preferred_languages={patient?.preferredLanguages
                            ?.map(formatLanguageLabel)
                            .join(", ")}
                        grade_level={patientGradeLevel}
                        availability={{
                            days: patient?.daysAvailable,
                            times: patient?.timeAvailable
                        }}
                        location={patientLocation}
                        inquired_at={formatDate(priorityAttribute?.inquiredAt)}
                        providers={
                            <ChipGroup
                                items={(providers?.length
                                    ? providers
                                    : [
                                          {
                                              firstName: "Not",
                                              lastName: "Assigned"
                                          }
                                      ]
                                ).map((provider) => ({
                                    label: `${provider?.firstName} ${provider?.lastName}`
                                }))}
                                size="sm"
                                color={"var(--mantine-color-gray-6)"}
                                limit={1}
                                onClick={() => {
                                    if (!patient?.patientId) return;
                                    manageProvidersForPatient.open({
                                        patientId: patient.patientId
                                    });
                                }}
                            />
                        }
                        status={
                            <ChipWithMenu
                                currentItem={{
                                    value: priorityStatus?.name,
                                    color: priorityStatus?.colorHex
                                }}
                                setCurrentItem={(status) => {
                                    if (!status.id) return;
                                    handleSetStatus({
                                        priorityStatusId: status.id,
                                        name: status.value,
                                        colorHex: status.color,
                                        sortOrder: status.sortOrder ?? 0
                                    });
                                }}
                                menuItems={statusMenuItems}
                                fontColor="var(--mantine-color-black)"
                                fontWeight={500}
                            />
                        }
                    />
                </Stack>
                <DrawerIf
                    cardTitle="Click to see Pipeline Notes"
                    drawerBreakpoint="md"
                    cardStyle={{
                        backgroundColor: "var(--mantine-color-primary-1)"
                    }}
                    drawerTitle="Pipeline Notes"
                    drawerContentStyle={{ marginTop: "auto", flex: 1 }}
                >
                    <PriorityNotesList
                        priorityEntryId={selectedEntryId as UUID}
                        caregiverId={priorityEntry?.caregiverId as UUID}
                        patientId={priorityEntry?.patientId as UUID}
                    />
                </DrawerIf>
                <Stack
                    gap="xs"
                    w={{ base: "100%", lg: 300 }}
                    miw={{ lg: 300 }}
                    style={{ overflowY: "auto" }}
                >
                    <TagsList
                        priorityAttributeId={
                            priorityAttribute?.priorityAttributeId
                        }
                        icon={LabelOutlined}
                        title="Tags"
                        tags={titleCaseTags}
                    />
                    <ChipListCard
                        icon={MedicalInformationOutlined}
                        title="Diagnoses"
                        chips={patientConditions}
                        chipFontWeight={500}
                        chipColor="var(--mantine-color-black)"
                        chipBackgroundColor="var(--mantine-color-gray-2)"
                    />
                    <ChipListCard
                        icon={VolunteerActivismOutlined}
                        title="Therapies Needed"
                        chips={patientTherapies}
                        chipFontWeight={500}
                        chipColor="var(--mantine-color-black)"
                        chipBackgroundColor="var(--mantine-color-gray-2)"
                        highlights={[therapyToHighlight || ""]}
                    />
                    <ChipListCard
                        icon={InterestsOutlined}
                        title="Skills / Interventions"
                        chips={patientSkills}
                        chipFontWeight={500}
                        chipColor="var(--mantine-color-black)"
                        chipBackgroundColor="var(--mantine-color-gray-2)"
                    />
                    <ChipListCard
                        icon={RequestQuoteOutlined}
                        title="Insurances"
                        chips={patientInsurances}
                        chipFontWeight={500}
                        chipColor="var(--mantine-color-black)"
                        chipBackgroundColor="var(--mantine-color-gray-2)"
                    />
                    <FileAccordion
                        caregiverId={priorityEntry?.caregiverId}
                        patientId={priorityEntry?.patientId}
                        handlers={{
                            handleEditFile: (fileInfo: UpdateFileInput) =>
                                handleOpenUploadFileModal(fileInfo),
                            handleOpenFilePreviewModal: (
                                fileId: string,
                                fileType: string,
                                tags?: ReadonlyArray<
                                    string | null | undefined
                                > | null
                            ) =>
                                handleOpenFilePreviewModal(
                                    fileId,
                                    fileType,
                                    tags
                                ),
                            handleDownloadFile: (
                                fileId: string,
                                name: string
                            ) => handleDownloadFile(fileId, name),
                            handleNewTab: (fileId: string) =>
                                handleNewTab(fileId),
                            handleDelete: (fileId: string) =>
                                handleDeleteFile(fileId)
                        }}
                    />
                </Stack>
            </Flex>
        </AutoHeightContainer>
    );
};
